import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useQuoteBasket, useCart, useAuth, useAuthDialog } from '../App';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { ExternalLink, CheckCircle2, FileText, Megaphone, ShoppingCart, Lock, Check } from 'lucide-react';
import JsonLd from '../components/JsonLd';
import Navbar from '../components/Navbar';
import { QuoteRFQModal } from '../components/QuoteRFQModal';
import SearchWithSuggestions from '../components/SearchWithSuggestions';
import NotFound from './NotFound';
import { PartnerSearchResults } from '../components/PartnerSearchResults';
import { useCurrency, displayPrice, indicativePriceRange, extractInrValue, CurrencySelector, PriceInfoTooltip } from '../utils/currency';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Tracking helper ──────────────────────────────────────────────────────── */
function trackAd(adId, eventType) {
  if (!adId) return;
  const token = sessionStorage.getItem('token') || '';
  fetch(`${API}/api/ads/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ ad_id: adId, event_type: eventType, page_url: window.location.pathname }),
  }).catch(() => {});
}

/* ── Product Detail Sidebar Ad ────────────────────────────────────────────── */
function ProductDetailAd() {
  const [ads, setAds]       = useState([]);
  const [current, setCurrent] = useState(0);
  const [fade, setFade]     = useState(true);
  const trackedRef          = useRef({});

  useEffect(() => {
    fetch(`${API}/api/public/product-detail-ad`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : { ads: [] })
      .then(d => setAds(d.ads || (d.ad ? [d.ad] : [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const ad = ads[current];
    if (ad && !trackedRef.current[ad.id]) {
      trackedRef.current[ad.id] = true;
      trackAd(ad.id, 'impression');
    }
  }, [ads, current]);

  useEffect(() => {
    if (ads.length < 2) return;
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => { setCurrent(c => (c + 1) % ads.length); setFade(true); }, 280);
    }, 6000);
    return () => clearInterval(t);
  }, [ads.length]);

  if (!ads.length) return null;
  const ad = ads[current];
  if (!ad?.image_url) return null;
  const imgSrc = ad.image_url.startsWith('/') ? `${API}${ad.image_url}` : ad.image_url;

  return (
    <div
      data-testid="product-detail-ad"
      className="rounded-xl overflow-hidden cursor-pointer border border-gray-100 shadow-sm"
      style={{ opacity: fade ? 1 : 0, transition: 'opacity 0.28s' }}
      onClick={() => {
        if (ad.cta_target) {
          trackAd(ad.id, 'click');
          window.open(ad.cta_target, '_blank', 'noopener');
        }
      }}
    >
      <img
        src={imgSrc}
        alt="Sponsored"
        className="w-full block min-h-[200px] object-cover"
        onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
      />
      <div className="bg-white px-3 py-1.5 text-right">
        <span className="text-[9px] text-gray-400 font-medium uppercase tracking-widest">Sponsored</span>
      </div>
    </div>
  );
}

/** 4-stage image fallback: direct → proxy → brand logo → gradient placeholder */
function PDPImage({ src, name, brandLogoUrl }) {
  const initStage = src ? 'direct' : (brandLogoUrl ? 'brand' : 'fallback');
  const [stage, setStage] = useState(initStage);
  const [loaded, setLoaded] = useState(false);
  const proxied = src ? `${API}/api/proxy-image?url=${encodeURIComponent(src)}` : null;

  const activeSrc =
    stage === 'direct' ? src :
    stage === 'proxy'  ? proxied :
    stage === 'brand'  ? brandLogoUrl :
    null;

  const handleError = () => {
    setLoaded(false);
    if (stage === 'direct' && proxied)       { setStage('proxy');    return; }
    if (stage === 'proxy'  && brandLogoUrl)  { setStage('brand');    return; }
    if (stage === 'brand')                   { setStage('fallback'); return; }
    setStage('fallback');
  };

  return (
    <div className="w-full aspect-square rounded-2xl border border-gray-100 bg-gray-50 flex items-center justify-center overflow-hidden relative">
      {activeSrc ? (
        <>
          {!loaded && (
            <div className="absolute inset-0 bg-gray-50 animate-pulse" />
          )}
          <img
            src={activeSrc}
            alt={name}
            loading="eager"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={handleError}
            className={`w-full h-full transition-opacity duration-300 ${stage === 'brand' ? 'object-contain p-8' : 'object-contain p-4'}`}
            style={{ opacity: loaded ? 1 : 0 }}
          />
        </>
      ) : (
        <div className="text-5xl font-black text-gray-200">
          {(name || '?').charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
}

/** Build a clean URL slug from a product.
 *  Priority: catalog-number (SEO-friendly, matches Google-indexed URLs)
 *            → UUID fallback (for purely-numeric or missing catalog numbers).
 *  Underscores, dots and slashes in catalog numbers are converted to hyphens
 *  (e.g. TM 2267_500GM → tm-2267-500gm, BLS.2300.34 → bls-2300-34).
 *  Purely numeric catalog numbers (e.g. BRAND/TPP integer IDs) always use UUID
 *  to avoid integer/string type mismatch in the database. */
export function productSlug(product) {
  const name = (product.name || product.product_name || '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

  // PRIORITY 1: Catalog number (any format — including purely-numeric)
  // Backend resolves numeric suffixes via integer MatchValue in Qdrant.
  const rawCat = String(product.catalog_number || '').trim();
  if (rawCat) {
    const cat = rawCat.toLowerCase()
      .replace(/[\s./\\_]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    if (cat) return name ? `${name}-${cat}` : cat;
  }

  // PRIORITY 2: UUID fallback — only for products with no catalog number at all
  const id     = product.id || '';
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  if (isUUID) return name ? `${name}-${id}` : id;

  return name || id || 'product';
}

/**
 * Returns the full URL path for a product, including ?pack= query param
 * when pack_size is present (distinguishes pack-size variants that share
 * the same catalog number, e.g. Loba Chemie 0190000100 in 25KG / 1KG / 100GM).
 */
export function productUrl(product) {
  const slug = productSlug(product);
  const pack = (product.pack_size || '').trim();
  return pack ? `/product/${slug}?pack=${encodeURIComponent(pack)}` : `/product/${slug}`;
}

// Matches a UUID anywhere in the slug (old Qdrant-generated slugs)
const UUID_IN_SLUG_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export default function ProductPage() {
  const { productId } = useParams();
  const navigate      = useNavigate();
  const location      = useLocation();
  const fromSearch    = location.state?.from === 'search';
  const fromCategory  = location.state?.from === 'category';
  const fromHomepage  = location.state?.from === 'homepage';

  // Navigate back to the search workspace preserving the query and conversation turns.
  // ws_back_query is intentionally left in sessionStorage so that BioMallHome's
  // mount effect can consume it to re-enter the workspace state. ws_turns_cache
  // (written by ConversationalWorkspace) is then used to rehydrate all previous turns.
  const goBackToSearch = () => {
    // Priority 1: came directly from a category page — go back there
    if (location.state?.from === 'category' && location.state?.categorySlug) {
      navigate(`/category/${location.state.categorySlug}`);
      return;
    }
    const wsQuery = sessionStorage.getItem('ws_back_query');
    if (wsQuery && wsQuery.trim()) {
      // Came from workspace — navigate(-1) restores the workspace route.
      // Do NOT remove ws_back_query here; BioMallHome reads it on mount.
      navigate(-1);
    } else {
      sessionStorage.removeItem('ws_back_query');
      const lastSearch = sessionStorage.getItem('last_search');
      if (lastSearch && lastSearch.trim()) {
        navigate(`/?q=${encodeURIComponent(lastSearch.trim())}`);
      } else {
        navigate(-1);
      }
    }
  };
  const fromCatSlug   = location.state?.categorySlug || '';
  const fromCatName   = location.state?.categoryName || '';
  const [product, setProduct]         = useState(null);
  const [canonicalSlugState, setCanonicalSlugState] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [notFound, setNotFound]       = useState(false);
  const [related, setRelated]         = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [publications, setPublications]     = useState([]);
  const [pubLoading, setPubLoading]         = useState(false);

  // Inline search state
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);

  const { quoteItems, addToQuote, removeFromQuote, clearQuote } = useQuoteBasket();
  const { addItem: addToEnquiryCart, isInCart, itemCount: enquiryCount } = useCart();
  const { user } = useAuth();
  const { openLabAuth } = useAuthDialog();
  const isLoggedIn = !!user;
  const isAdded = product ? quoteItems.some(q => q.id === product.id) : false;
  const isInEnquiry = product ? isInCart(product.id || product.catalog_number) : false;
  const [showRFQ, setShowRFQ] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [interestState, setInterestState] = useState({ buy: 'idle', sell: 'idle' });
  const currencyInfo = useCurrency();

  // Auto-submit enquiry after login if user clicked Enquire while logged out
  useEffect(() => {
    if (isLoggedIn && product && sessionStorage.getItem('pending_product_enquiry') === 'buy') {
      sessionStorage.removeItem('pending_product_enquiry');
      handleProductInterest('buy');
    }
  }, [isLoggedIn, product]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleProductInterest(interestType) {
    const token = sessionStorage.getItem('lab_session_token') || sessionStorage.getItem('token') || '';
    if (!token) {
      if (interestType === 'buy') sessionStorage.setItem('pending_product_enquiry', 'buy');
      openLabAuth(`Sign in to ${interestType === 'buy' ? 'register buying interest' : 'list as seller'}`);
      return;
    }
    if (interestState[interestType] !== 'idle') return;
    setInterestState(s => ({ ...s, [interestType]: 'loading' }));
    try {
      const res = await fetch(`${API}/api/user/product-interests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          product_name:     product?.product_name || product?.name || '',
          brand_name:       product?.brand || '',
          catalogue_number: product?.catalog_number || '',
          interest_type: interestType,
          currency: localStorage.getItem('biomall_currency') || 'INR',
        }),
      });
      if (res.ok) {
        setInterestState(s => ({ ...s, [interestType]: 'done' }));
        if (interestType === 'buy') {
          toast.success('Enquiry sent! Relevant distributors will be notified.');
        } else {
          toast.success('Listed! Buyers looking for this product will be notified.');
        }
      } else {
        setInterestState(s => ({ ...s, [interestType]: 'idle' }));
        // 401 means the session is invalid / user is not a lab user → open login
        if (res.status === 401) {
          openLabAuth(`Sign in to ${interestType === 'buy' ? 'register buying interest' : 'list as a seller'}`);
          return;
        }
        try {
          const errorCode = res.headers.get('X-BioMall-Error') || '';
          if (errorCode === 'buyer_cannot_sell') {
            toast.error('Only distributors and manufacturers can sell products on BioMall.ai.');
          } else if (errorCode === 'pending_verification') {
            toast.warning('Your account is pending admin verification. You will be notified once verified.');
          } else {
            toast.error('Something went wrong. Please try again.');
          }
        } catch {
          toast.error('Something went wrong. Please try again.');
        }
      }
    } catch {
      setInterestState(s => ({ ...s, [interestType]: 'idle' }));
    }
  }

  // Fetch publications when product is loaded
  useEffect(() => {
    if (!productId) return;
    setPubLoading(true);
    fetch(`${API}/api/product/${productId}/publications`)
      .then(r => r.json())
      .then(d => setPublications(d.publications || []))
      .catch(() => setPublications([]))
      .finally(() => setPubLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Search partner brands inline — searches across ALL partner brand catalogues
  const handleNavSearch = useCallback(async (q) => {
    setSearchQuery(q);
    if (!q) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res  = await fetch(`${API}/api/partner-products-search?search=${encodeURIComponent(q)}&limit=200`);
      const data = await res.json();
      setSearchResults(data.products || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // When a product isn't found, search for it in-place instead of navigating
  // away to the homepage — keeps the original URL so Google doesn't treat
  // this as a confusing "soft 404" (page content = homepage, status = 200).
  useEffect(() => {
    if (!notFound) return;
    const tokens = (productId || '').split(/[-_]/);
    const catNoPattern = /^[a-z]{1,5}\d{3,}$/i;
    const digitLeadPattern = /^\d{4,}[a-z]{2,}/i;
    const seen = new Set();
    const clean = tokens.filter(t => {
      const lower = t.toLowerCase();
      if (catNoPattern.test(lower)) return false;
      if (digitLeadPattern.test(lower)) return false;
      if (/^\d+$/.test(lower)) return false;
      if (seen.has(lower)) return false;
      seen.add(lower);
      return true;
    });
    const searchTerm = clean.join(' ').trim();
    if (searchTerm) handleNavSearch(searchTerm);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFound, productId]);

  useEffect(() => {
    setLoading(true);
    setRelated([]);
    setSearchQuery('');
    setSearchResults([]);
    // Pass ?pack= query param if present in URL — disambiguates pack-size variants
    // that share the same catalog slug (e.g. Loba Chemie 0190000100 25KG/1KG/100GM).
    const urlParams = new URLSearchParams(location.search);
    const packParam = urlParams.get('pack') || '';
    const fetchUrl  = packParam
      ? `${API}/api/product/${productId}?pack=${encodeURIComponent(packParam)}`
      : `${API}/api/product/${productId}`;
    fetch(fetchUrl)
      .then(r => { if (r.status === 404) { setNotFound(true); return null; } return r.json(); })
      .then(d => {
        if (d) {
          setProduct(d);
          // Compute canonical slug from product data
          const canonicalSlug = productSlug(d);
          setCanonicalSlugState(canonicalSlug);
          // Only do a real navigate() redirect for legacy UUID-based slugs (old Qdrant format).
          // For other slug mismatches (e.g. product name changed in DB), silently update the
          // URL bar via history.replaceState so Google does NOT see a redirect, while the
          // canonical <link> tag below signals the preferred URL.
          if (canonicalSlug && canonicalSlug !== productId) {
            if (UUID_IN_SLUG_RE.test(productId)) {
              navigate(`/product/${canonicalSlug}`, { replace: true });
            } else {
              window.history.replaceState({}, '', `/product/${canonicalSlug}`);
            }
          }
          // Fetch related products in background
          setRelatedLoading(true);
          fetch(`${API}/api/product/${productId}/related?limit=8`)
            .then(r => r.json())
            .then(rd => setRelated(rd.products || []))
            .catch(() => setRelated([]))
            .finally(() => setRelatedLoading(false));
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        searchPlaceholder="Describe your need, offer, or announcement.."
      />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl p-6 md:p-8 animate-pulse">
          <div className="h-4 bg-gray-100 rounded w-48 mb-6" />
          <div className="flex flex-col sm:flex-row gap-8">
            <div className="w-full sm:w-64 h-48 sm:h-64 bg-gray-100 rounded-2xl flex-shrink-0" />
            <div className="flex-1 space-y-4">
              <div className="h-8 bg-gray-100 rounded w-3/4" />
              <div className="h-4 bg-gray-100 rounded w-1/2" />
              <div className="h-4 bg-gray-100 rounded w-1/3" />
              <div className="h-4 bg-gray-100 rounded w-1/4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (notFound) {
    // Stay on the original URL and render helpful search results in-place —
    // do NOT navigate away to the homepage (that causes Google to see
    // homepage content at a product URL and flag it as a "soft 404").
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>Product Not Available | BioMall.ai</title>
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={`https://biomall.ai/product/${productId}`} />
        </Helmet>
        <Navbar searchPlaceholder="Describe your need, offer, or announcement.." />
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="sm:hidden mb-3">
            <SearchWithSuggestions
              value={searchQuery}
              onChange={v => { setSearchQuery(v); if (v) handleNavSearch(v); else setSearchResults([]); }}
              onSubmit={q => { if (q) handleNavSearch(q); }}
              placeholder="Search products by name, Cat. No..."
              testId="product-not-found-mobile-search"
            />
          </div>
          <p className="text-sm text-gray-500 mb-4" data-testid="product-not-found-message">
            We couldn't find that exact product — here's what we found instead:
          </p>
          <PartnerSearchResults
            query={searchQuery}
            results={searchResults}
            searching={searching}
            onClear={() => { setSearchQuery(''); setSearchResults([]); }}
            isLoggedIn={isLoggedIn}
            openLabAuth={openLabAuth}
            quoteItems={quoteItems}
            addToQuote={addToQuote}
            showBrandStoreBadge={false}
          />
        </div>
      </div>
    );
  }
  if (!product) return <NotFound />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <link rel="canonical" href={`https://biomall.ai/product/${canonicalSlugState || productId}`} />
        <title>{`Buy ${product.product_name || product.name} Online | ${product.brand || 'BioMall.ai'} | Request Quote — BioMall.ai`}</title>
        <meta name="description" content={`Buy ${product.product_name || product.name} online${product.brand ? ` from ${product.brand}` : ''}. Cat #${product.catalog_number || ''}. Request a quote instantly on BioMall.ai.`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`Buy ${product.product_name || product.name} Online | ${product.brand || 'BioMall.ai'}`} />
        <meta property="og:description" content={`Buy ${product.product_name || product.name} online from verified distributors worldwide.${product.brand ? ` ${product.brand}` : ''} Cat #${product.catalog_number || ''}. Request quote on BioMall.ai.`} />
        <meta property="og:url" content={`https://biomall.ai/product/${canonicalSlugState || productId}`} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={product.image_url || 'https://biomall.ai/og-image.png'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Buy ${product.product_name || product.name} Online | ${product.brand || 'BioMall.ai'}`} />
        <meta name="twitter:description" content={`Buy ${product.product_name || product.name} online from verified distributors worldwide.${product.brand ? ` ${product.brand}` : ''} Cat #${product.catalog_number || ''}. Request quote on BioMall.ai.`} />
        <meta name="twitter:image" content={product.image_url || 'https://biomall.ai/og-image.png'} />
      </Helmet>
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "BioMall.ai", "item": "https://biomall.ai" },
          { "@type": "ListItem", "position": 2, "name": "Brand Store", "item": "https://biomall.ai/brands" },
          ...(product.brand ? [{ "@type": "ListItem", "position": 3, "name": product.brand, "item": `https://biomall.ai/brands/${product.brand.toLowerCase().replace(/\s+/g, '-')}` }] : []),
          { "@type": "ListItem", "position": product.brand ? 4 : 3, "name": product.product_name || product.name, "item": `https://biomall.ai/product/${productId}` }
        ]
      }} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "Product",
        "name": product.product_name || product.name,
        "description": product.description || `${product.product_name || product.name}${product.brand ? ` by ${product.brand}` : ''}. Cat. No. ${product.catalog_number || ''}.`,
        ...(product.brand && { "brand": { "@type": "Brand", "name": product.brand } }),
        "image": [product.image_url || "https://biomall.ai/og-image.png"],
        ...(product.catalog_number && { "mpn": product.catalog_number }),
        ...(extractInrValue(product.price) ? { "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "url": `https://biomall.ai/product/${productId}`,
          "seller": { "@type": "Organization", "name": "BioMall.ai" },
          // Google requires "offers" to always include a price — omit the whole
          // block (not just price) when no real price data exists, rather than
          // claiming an Offer without a required price field.
          "price": String(extractInrValue(product.price)),
          "priceCurrency": "INR",
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "INR" },
            "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "IN" },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 3, "unitCode": "DAY" },
              "transitTime": { "@type": "QuantitativeValue", "minValue": 2, "maxValue": 7, "unitCode": "DAY" }
            }
          },
          "hasMerchantReturnPolicy": {
            "@type": "MerchantReturnPolicy",
            "applicableCountry": "IN",
            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
            "merchantReturnDays": 7,
            "returnMethod": "https://schema.org/ReturnByMail",
            "returnFees": "https://schema.org/FreeReturn"
          }
        } } : {})
      }} />
      <Navbar
        searchPlaceholder="Describe your need, offer, or announcement.."
      />
      <div className="max-w-6xl mx-auto px-3 md:px-4 py-4 md:py-8">

        {/* ── Back to Search — shown at top when navigated from search workspace ── */}
        {fromSearch && !searchQuery && (
          <button
            onClick={goBackToSearch}
            className="inline-flex items-center gap-1.5 mb-4 text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline"
            data-testid="back-to-search-btn"
          >
            ← Back to Search
          </button>
        )}

        {/* ── Inline search results ─────────────────────────────── */}
        {/* Mobile search bar with AI suggestions */}
        <div className="sm:hidden mb-3">
          <SearchWithSuggestions
            value={searchQuery}
            onChange={v => { setSearchQuery(v); if (v) handleNavSearch(v); else setSearchResults([]); }}
            onSubmit={q => { if (q) handleNavSearch(q); }}
            placeholder="Search products by name, Cat. No..."
            testId="product-page-mobile-search"
          />
        </div>
        {searchQuery ? (
          <PartnerSearchResults
            query={searchQuery}
            results={searchResults}
            searching={searching}
            onClear={() => { setSearchQuery(''); setSearchResults([]); }}
            backLabel="Back to product"
            isLoggedIn={isLoggedIn}
            openLabAuth={openLabAuth}
            quoteItems={quoteItems}
            addToQuote={addToQuote}
            onCardClick={() => { setSearchQuery(''); setSearchResults([]); }}
            showBrandStoreBadge={false}
          />
        ) : (
          <div>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
          <Link to="/brands" className="hover:text-blue-600 transition-colors">Brand Store</Link>
          <span>/</span>
          <Link to={`/brands/${product.brand_slug}`} className="hover:text-blue-600 transition-colors">{product.brand}</Link>
          <span>/</span>
          <span className="text-gray-700 truncate max-w-xs">{product.name}</span>
        </div>

        <div className="flex gap-5 items-center">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 flex-1 min-w-0">
          <div className="flex flex-col md:flex-row gap-8">

            {/* Image */}
            <div className="flex-shrink-0 w-full md:w-64">
              <PDPImage
                src={product.image_url}
                name={product.name}
                brandLogoUrl={product.brand_logo_url || ''}
              />
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              {/* Badges */}
              <div className="flex items-center gap-2 mb-3">
                {product.is_partner ? (
                  <span className="text-[10px] bg-blue-600 text-white px-2.5 py-1 rounded font-bold" data-testid="brand-store-badge">BRAND STORE</span>
                ) : (
                  <span className="text-[10px] bg-gray-600 text-white px-2.5 py-1 rounded font-bold" data-testid="general-catalogue-badge">GENERAL CATALOGUE</span>
                )}
              </div>

              <h1 className="text-xl md:text-2xl font-black text-gray-900 leading-tight mb-1" data-testid="product-page-title">
                {product.name}
              </h1>

              {/* Catalog info */}
              <p className="text-sm text-gray-500 mb-4">
                {product.catalog_number && `${product.catalog_number} · `}{product.pack_size || ''}
              </p>

              {/* Brand info with initials circle (Brand Store only) */}
              {product.is_partner && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">{(product.brand || 'B').slice(0, 2).toUpperCase()}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Link to={`/brands/${product.brand_slug}`} className="text-sm font-semibold text-gray-900 hover:text-blue-600">{product.brand}</Link>
                      <CheckCircle2 className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                    <p className="text-xs text-gray-400">Verified Partner</p>
                  </div>
                </div>
              )}

              {/* Brand link for General Catalogue */}
              {!product.is_partner && product.brand && (
                <p className="text-sm text-gray-500 mb-4">
                  <Link to={`/brands/${product.brand_slug}`} className="text-blue-600 hover:underline">{product.brand}</Link>
                  {product.catalog_number && ` · Cat: ${product.catalog_number}`}
                </p>
              )}

              {/* Price display — single list price for Partner Brand, indicative range for General Catalogue */}
              {product.is_partner ? (
                !isLoggedIn ? null : (
                  <div className="mb-4" data-testid="product-page-price">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                        {displayPrice(product.price, currencyInfo) || 'POR'}
                        <PriceInfoTooltip />
                      </div>
                      {/* Currency selector — inline with price */}
                      <CurrencySelector />
                    </div>
                  </div>
                )
              ) : (
                // General Catalogue: show indicative ±20% range
                (() => {
                  const priceDisplay = indicativePriceRange(product.price, currencyInfo, 'approx.');
                  if (!priceDisplay) return null;

                  return (
                    <div className="mb-4" data-testid="product-page-price">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                          {priceDisplay}
                          <PriceInfoTooltip />
                        </div>
                        {/* Currency selector — inline with price */}
                        <CurrencySelector />
                      </div>
                    </div>
                  );
                })()
              )}

              {/* CTAs — different for Brand Store vs General */}
              {product.is_partner ? (
                <div className="space-y-2 mb-4">
                  {!isLoggedIn ? (
                    <>
                      <button
                        data-testid="product-page-view-price-btn"
                        onClick={() => openLabAuth('Sign in to view price')}
                        className="w-full px-5 py-3 text-sm font-bold rounded-xl transition-colors bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center gap-2"
                      >
                        <Lock className="w-4 h-4" /> View Price
                      </button>
                      <p className="text-[10px] text-center text-gray-400">Sign in to see pricing and request a quote.</p>
                    </>
                  ) : (
                    <>
                      <button
                        data-testid="product-page-request-quote-btn"
                        disabled={isAdded}
                        onClick={() => { if (!isAdded) addToQuote(product); }}
                        className={`w-full px-5 py-3 text-sm font-bold rounded-xl transition-colors ${
                          isAdded ? 'bg-green-100 text-green-700 border border-green-200 cursor-default' : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {isAdded ? '✓ Added to Quote' : (<><FileText className="w-4 h-4 inline mr-2" />Add to Quote</>)}
                      </button>
                      <p className="text-[10px] text-center text-gray-400">Direct from BioMall or manufacturer. Curated, fast.</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-2 mb-4">
                  {/* Enquire */}
                  <div className="flex gap-2">
                    <button
                      data-testid="product-page-buy-btn"
                      onClick={() => handleProductInterest('buy')}
                      disabled={interestState.buy !== 'idle'}
                      className={`flex-1 px-4 py-2.5 text-sm font-bold rounded-xl border-2 transition-colors ${interestState.buy === 'done' ? 'bg-blue-50 border-blue-200 text-blue-500' : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'}`}
                    >
                      {interestState.buy === 'done' ? '✓ Registered' : interestState.buy === 'loading' ? '…' : 'Enquire'}
                    </button>
                  </div>
                  <p className="text-[11px] text-teal-600 font-semibold mt-1">Reach 3+ verified distributors</p>
                </div>
              )}

              {/* External product page link — ONLY for non-Brand Store */}
              {!product.is_partner && product.product_url && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-3" data-testid="external-product-link">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Manufacturer product page</p>
                      <a href={product.product_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                        {product.product_url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}{product.product_url.length > 70 ? '...' : ''}
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Brand Store notice */}
              {product.is_partner && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-start gap-2" data-testid="brand-store-notice">
                  <CheckCircle2 className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-yellow-800">Brand Store Product</p>
                    <p className="text-[10px] text-yellow-700">No external links. Get your quote directly here.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>{/* end product card */}
          {/* Sidebar ad — centered beside product card */}
          <div className="hidden lg:flex flex-col justify-center w-80 flex-shrink-0 self-start pt-2">
            <ProductDetailAd />
          </div>
        </div>

        {/* Back + Brand Store link */}
        <div className="mt-5 flex items-center gap-4 flex-wrap">
          <button
            onClick={goBackToSearch}
            className="text-sm text-blue-600 hover:underline"
            data-testid="product-page-back-btn"
          >
            {fromHomepage
              ? '← Back to homepage'
              : fromCategory
              ? `← Back to ${fromCatName || 'category'}`
              : fromSearch
              ? '← Back to search results'
              : '← Go back'}
          </button>
          {product.brand_slug && product.is_partner && (
            <button
              onClick={() => navigate(`/brands/${product.brand_slug}`)}
              className="text-sm text-blue-600 hover:underline"
              data-testid="product-page-brand-store-btn"
            >
              Go to {product.brand} brand store →
            </button>
          )}
        </div>

        {/* Documents & References Tabs */}
        <ProductDocumentsSection product={product} productId={productId} publications={publications} pubLoading={pubLoading} />

        {/* Related Products */}
        {(relatedLoading || related.length > 0) && (
          <div className="mt-10">
            <h2 className="text-base font-black text-gray-900 mb-4">
              More from {product.brand}
            </h2>
            {relatedLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                    <div className="w-full aspect-square bg-gray-100 rounded-xl mb-3" />
                    <div className="h-3 bg-gray-100 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {related.map(r => (
                  <RelatedCard
                    key={r.id}
                    product={r}
                    inQuote={quoteItems.some(q => q.id === r.id)}
                    onAdd={() => addToQuote(r)}
                    onClick={() => navigate(productUrl(r))}
                  />
                ))}
              </div>
            )}
          </div>
        )}

          </div>
        )}

      </div>

      {/* Floating quote basket */}
      {quoteItems.length > 0 && !showRFQ && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowRFQ(true)}
            className="flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-full shadow-xl transition-colors font-bold text-sm"
            data-testid="product-page-view-quote-btn"
          >
            <span className="w-6 h-6 bg-white text-blue-600 rounded-full flex items-center justify-center text-xs font-black">
              {quoteItems.length}
            </span>
            Add to Quote
          </button>
        </div>
      )}

      {showRFQ && (
        <QuoteRFQModal
          brand={null}
          items={quoteItems}
          onRemoveItem={removeFromQuote}
          onSubmitDone={clearQuote}
          onClose={() => setShowRFQ(false)}
        />
      )}
    </div>
  );
}

function Row({ label, value, mono, highlight }) {
  return (
    <>
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide self-center">{label}</div>
      <div className={`text-sm self-center ${highlight ? 'font-bold text-blue-700' : 'text-gray-800'} ${mono ? 'font-mono' : ''}`}>
        {value}
      </div>
    </>
  );
}

function RelatedCard({ product, inQuote, onAdd, onClick }) {
  return (
    <div
      onClick={onClick}
      className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-blue-200 hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col"
      data-testid={`related-card-${product.catalog_number || product.id}`}
    >
      {/* Image */}
      <div className="w-full aspect-square bg-gray-50 overflow-hidden">
        <PDPImage
          src={product.image_url}
          name={product.name}
          brandLogoUrl={product.brand_logo_url || ''}
        />
      </div>

      {/* Info */}
      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <div className="text-xs font-bold text-gray-900 leading-snug line-clamp-2">
          {product.name}
        </div>
        {product.catalog_number && (
          <div className="text-xs text-gray-400 font-mono">{product.catalog_number}</div>
        )}
        {product.category && (
          <div className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full w-fit truncate max-w-full">
            {product.category}
          </div>
        )}
        <button
          data-testid={`related-add-quote-${product.id}`}
          disabled={inQuote}
          onClick={e => { e.stopPropagation(); if (!inQuote) onAdd(); }}
          className={`mt-auto w-full py-1.5 text-xs font-bold rounded-lg transition-colors ${
            inQuote
              ? 'bg-green-50 text-green-700 border border-green-200 cursor-default'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {inQuote ? '✓ Added' : '+ Quote'}
        </button>
      </div>
    </div>
  );
}


// ─── Product Documents & References Section ───────────────────────────────────
function ProductDocumentsSection({ product, productId, publications, pubLoading }) {
  const [activeTab, setActiveTab] = useState('details');
  if (!product) return null;

  const tabs = [
    { key: 'details', label: 'Product Details' },
    { key: 'documents', label: 'Documents (SDS/TDS)' },
    { key: 'pubmed', label: 'PubMed References' },
  ];

  // Build document list from product data (only show entries with real URLs)
  const docs = [];
  if (product.sds_url) docs.push({ name: 'Safety Data Sheet (SDS)', url: product.sds_url, icon: 'pdf', desc: 'Safety information and handling precautions' });
  if (product.tds_url) docs.push({ name: 'Technical Data Sheet (TDS)', url: product.tds_url, icon: 'doc', desc: 'Product specifications & protocols' });
  if (product.coa_url) docs.push({ name: 'Certificate of Analysis (COA)', url: product.coa_url, icon: 'cert', desc: 'Quality assurance certificate' });

  const iconMap = {
    pdf: <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/></svg>,
    doc: <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/></svg>,
    cert: <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>,
  };

  return (
    <div className="mt-8" data-testid="product-documents-section">
      {/* Tab bar */}
      <div className="border-b border-gray-200 flex gap-0 mb-0 overflow-x-auto scrollbar-hide">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`product-tab-${tab.key}`}
            className={`px-3 md:px-4 py-2.5 text-xs md:text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
              activeTab === tab.key
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-400 border-transparent hover:text-gray-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'details' && (
        <div className="py-5" data-testid="product-tab-content-details">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {product.catalog_number && <div><span className="text-gray-500">Catalog No:</span> <span className="font-medium">{product.catalog_number}</span></div>}
              {product.cas_number && <div><span className="text-gray-500">CAS No:</span> <span className="font-medium">{product.cas_number}</span></div>}
              {product.category && <div><span className="text-gray-500">Category:</span> <span className="font-medium">{product.category}</span></div>}
              {product.pack_size && <div><span className="text-gray-500">Pack Size:</span> <span className="font-medium">{product.pack_size}</span></div>}
              {product.brand && <div><span className="text-gray-500">Brand:</span> <span className="font-medium">{product.brand}</span></div>}
              {product.storage && <div><span className="text-gray-500">Storage:</span> <span className="font-medium">{product.storage}</span></div>}
            </div>
            {(product.short_description || product.description) && !['nan', 'n/a', 'none', 'null', 'undefined'].includes((product.short_description || product.description).toLowerCase().trim()) && (
              <div className="mt-4 pt-4 border-t border-gray-200" data-testid="product-description-section">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</p>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{product.short_description || product.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="py-5 space-y-3" data-testid="product-tab-content-documents">
          {docs.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              No documents available for this product.
            </div>
          ) : docs.map((doc, i) => (
            <div key={i} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">{iconMap[doc.icon]}</div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{doc.name}</p>
                  <p className="text-xs text-gray-500">{doc.desc}</p>
                </div>
              </div>
              {doc.url ? (
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-blue-600 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50" data-testid={`doc-download-${i}`}>
                  Download
                </a>
              ) : (
                <span className="text-xs text-gray-400 italic">Request from supplier</span>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'pubmed' && (
        <div className="py-5 space-y-3" data-testid="product-tab-content-pubmed">
          {pubLoading ? (
            <div className="flex items-center justify-center py-10 gap-2 text-sm text-gray-400">
              <svg className="animate-spin w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
              Searching PubMed…
            </div>
          ) : publications.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <p className="text-sm mb-3">No publications found for this product.</p>
              <a href={`https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(product.name || '')}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline font-semibold"
                data-testid="pubmed-search-link">
                Search PubMed directly →
              </a>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-400 mb-2">{publications.length} publication{publications.length !== 1 ? 's' : ''} found on PubMed</p>
              {publications.map((pub, i) => (
                <a key={pub.pmid} href={pub.url} target="_blank" rel="noopener noreferrer"
                  className="block border border-gray-100 rounded-xl p-3.5 hover:border-blue-200 hover:bg-blue-50/30 transition-all"
                  data-testid={`publication-${i}`}>
                  <p className="text-xs font-semibold text-gray-800 leading-snug mb-1">{pub.title}</p>
                  {pub.authors.length > 0 && (
                    <p className="text-[10px] text-gray-500 mb-0.5">{pub.authors.join(', ')}{pub.authors.length === 4 ? ' et al.' : ''}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5">
                    {pub.journal && <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{pub.journal}</span>}
                    {pub.year && <span className="text-[10px] text-gray-400">{pub.year}</span>}
                    <span className="ml-auto text-[10px] text-blue-500 font-semibold">Read →</span>
                  </div>
                </a>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
